/**
 * OwnOrganisationsManager (#3371) — the read/write half of "Eigene
 * Organisationen". Mounted only while the section is open, which is what keeps
 * every React Query hook out of `<SettingsPage />`'s own render (see
 * ``OwnOrganisationsSection.tsx`` for why that matters).
 *
 * Rides the shipped generic entity-edit path — ``twinService.editEntity`` ->
 * ``PUT /v1/twin/entities/organization/{id}`` -> ``push_entity_edit`` ->
 * ``link_to_entities`` (``source == "user_edit"``) -> ``update_entity``. No new
 * endpoint, no ``farm_profile`` write verb, no migration. The pure decisions
 * (request shape, ``internal: null`` on remove, the optimistic fold) all live in
 * ``ownOrganisations.ts``; this file is the chrome and the mutation wiring.
 *
 * Laid out with the design system's settings rows (settings refactor): the
 * own-group list, then "Organisation hinzufügen" with the search.
 */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Button, Input, SettingsRow, SettingsSection } from '@hofsync/design-system';
import { useToast } from '../../contexts/ToastContext';
import { twinService } from '../../services/twinService';
import { useEntities } from '../../hooks/useTwin';
import { ownOrganisationKeys, useOwnOrganisations } from './useOwnOrganisations';
import {
    OWN_ORG_ENTITY_TYPE,
    OWN_ORG_MIN_QUERY_LENGTH,
    OWN_ORG_PICKER_PAGE_SIZE,
    applyOverlay,
    overlayEntry,
    ownOrganisationFieldUpdate,
    ownOrganisationOptions,
    ownOrganisationSearchTerm,
    pickableOrganisations,
    resolveOwnOrganisations,
    revertOverlay,
    type OwnOrgOverlay,
    type OwnOrganisationOption,
} from './ownOrganisations';

export interface OwnOrganisationsManagerProps {
    /** The optimistic overlay, owned by the SECTION so that collapsing and
     *  reopening does not resurrect an organisation the farmer just removed:
     *  the cached list is served from a 60 s `staleTime` and, until the worker
     *  drains, still reports the pre-edit truth. */
    overlay: OwnOrgOverlay;
    setOverlay: React.Dispatch<React.SetStateAction<OwnOrgOverlay>>;
}

/** Idle-time before a typed term reaches the wire — one read per word typed at
 *  speed, not one per keystroke. Same value as the Gegenpartei picker. */
const DEBOUNCE_MS = 250;

interface SearchResultsProps {
    term: string;
    excludedIds: ReadonlySet<string>;
    busyIds: ReadonlySet<string>;
    onAdd: (option: OwnOrganisationOption) => void;
}

/**
 * Searches organisations for one term. Mounted only for a term worth a read, so
 * an unopened picker constructs no query at all.
 */
function SearchResults({ term, excludedIds, busyIds, onAdd }: SearchResultsProps) {
    const { t } = useTranslation();
    const { entities, total, primaryKey, isLoading, isError } = useEntities(OWN_ORG_ENTITY_TYPE, undefined, {
        paginated: true,
        pageSize: OWN_ORG_PICKER_PAGE_SIZE,
        q: term,
    });
    const hits = pickableOrganisations(ownOrganisationOptions(entities, primaryKey), excludedIds);

    // An errored search is NOT "there is no such organisation" — saying it that
    // way asserts something false about the tenant's own twin. Failure gets its
    // own alert; only a read that actually answered may say "nothing found".
    if (isError && hits.length === 0) {
        return (
            <p role="alert">
                {t('settings.ownOrganisations.searchFailed', 'Organisationen konnten nicht durchsucht werden.')}
            </p>
        );
    }
    if (isLoading && hits.length === 0) {
        return (
            <p role="status">
                {t('settings.ownOrganisations.searching', 'Suche läuft …')}
            </p>
        );
    }
    // Measured against the page the SERVER returned, not the filtered list:
    // organisations dropped here are still rows this page covered, so counting
    // them as "further hits" would send the farmer searching for a duplicate.
    const hidden = Math.max(0, total - entities.length);

    // "Nothing found" is a claim about the SEARCH, so it keys off what the server
    // returned — not off the post-exclusion list. A term matching only
    // organisations already on the list found them; saying otherwise sends the
    // farmer looking for a record that is right there on the page above.
    //
    // And "ALL matches are already listed" is only true when the server page was
    // the whole answer: with a capped page whose visible rows happen to be
    // flagged, unseen matches remain, so the honest line names them instead.
    if (hits.length === 0) {
        if (entities.length === 0) {
            return (
                <p role="status">
                    {t('settings.ownOrganisations.searchEmpty', 'Keine Organisation gefunden.')}
                </p>
            );
        }
        return (
            <p role="status">
                {hidden > 0
                    ? `${t(
                          'settings.ownOrganisations.searchShownFlagged',
                          'Die angezeigten Treffer stehen bereits auf der Liste.'
                      )} ${hidden} ${t('settings.ownOrganisations.moreHits', 'weitere Treffer — Suche verfeinern.')}`
                    : t(
                          'settings.ownOrganisations.searchAllFlagged',
                          'Alle Treffer stehen bereits auf der Liste.'
                      )}
            </p>
        );
    }
    return (
        <>
            {hits.map((hit) => (
                <SettingsRow key={hit.id} label={hit.label} description={hit.sublabel || undefined}>
                    <Button
                        size="sm"
                        variant="outline"
                        // Composed rather than interpolated: a ``{{name}}``
                        // placeholder resolves differently under i18next and under
                        // a test's ``t`` stub, and this string is a locator.
                        aria-label={`${hit.label} ${t('settings.ownOrganisations.addAria', 'hinzufügen')}`}
                        disabled={busyIds.has(hit.id)}
                        onClick={() => onAdd(hit)}
                    >
                        {t('settings.ownOrganisations.add', 'Hinzufügen')}
                    </Button>
                </SettingsRow>
            ))}
            {hidden > 0 && (
                <p role="status">
                    {hidden} {t('settings.ownOrganisations.moreHits', 'weitere Treffer — Suche verfeinern.')}
                </p>
            )}
        </>
    );
}

export default function OwnOrganisationsManager({ overlay, setOverlay }: OwnOrganisationsManagerProps) {
    const { t } = useTranslation();
    const { showToast } = useToast();
    const queryClient = useQueryClient();
    const { data, isLoading, isError } = useOwnOrganisations();

    // EVERY in-flight id, not just the most recent one. A single slot re-enabled
    // the previous row's control the moment a second write started, which made
    // two overlapping writes ON THE SAME id reachable — and their rollbacks then
    // land LIFO against priors the earlier rollback already invalidated, leaving
    // a phantom own-organisation the server never accepted. Since the overlay now
    // outlives a collapse, that phantom would survive until a full page reload,
    // and an own-group flag suppresses revenue booking. Disabling every in-flight
    // id makes the interleaving unreachable instead of merely unlikely.
    const [busyIds, setBusyIds] = useState<ReadonlySet<string>>(new Set());

    const [raw, setRaw] = useState('');
    const [debounced, setDebounced] = useState('');
    useEffect(() => {
        const timer = setTimeout(() => setDebounced(raw), DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [raw]);

    const serverOptions = ownOrganisationOptions(data?.entities, data?.primary_key);
    const options = resolveOwnOrganisations(serverOptions, overlay);
    const flaggedIds = new Set(options.map((option) => option.id));

    const flagMutation = useMutation({
        mutationFn: ({ option, flagged }: { option: OwnOrganisationOption; flagged: boolean }) =>
            twinService.editEntity(OWN_ORG_ENTITY_TYPE, option.id, ownOrganisationFieldUpdate(flagged)),
        onMutate: ({ option, flagged }) => {
            setBusyIds((current) => new Set(current).add(option.id));
            // Capture THIS id's prior membership only. A whole-overlay snapshot
            // would look equivalent and silently discard a concurrent write's
            // successful change on rollback — see ``revertOverlay``. Reading the
            // rendered `overlay` is current: each click is its own event, so the
            // previous click's state update has already re-rendered by now.
            const prior = overlayEntry(overlay, option.id);
            setOverlay((current) => applyOverlay(current, option, flagged));
            return { id: option.id, prior };
        },
        onError: (_error, _variables, context) => {
            if (context) {
                setOverlay((current) => revertOverlay(current, context.id, context.prior));
            }
            showToast(
                t('settings.ownOrganisations.saveFailed', 'Änderung konnte nicht gespeichert werden.'),
                'error',
            );
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ownOrganisationKeys.all });
        },
        onSettled: (_data, _error, { option }) =>
            setBusyIds((current) => {
                const next = new Set(current);
                next.delete(option.id);
                return next;
            }),
    });

    const term = ownOrganisationSearchTerm(debounced);
    const tooShort = raw.trim().length > 0 && ownOrganisationSearchTerm(raw) === null;

    return (
        <div className="own-organisations">
            <SettingsSection title={t('settings.ownOrganisations.listTitle', 'Ausgewählte Organisationen')}>
                {isError && options.length === 0 ? (
                    <SettingsRow
                        label={
                            <span role="alert">
                                {t('settings.ownOrganisations.loadFailed', 'Eigene Organisationen konnten nicht geladen werden.')}
                            </span>
                        }
                    />
                ) : isLoading && options.length === 0 ? (
                    <SettingsRow
                        label={
                            // Fallback matches de.json's `common.loading` verbatim, so the
                            // string a test sees is the string the app renders.
                            <span role="status">{t('common.loading', 'Lädt...')}</span>
                        }
                    />
                ) : options.length === 0 ? (
                    <SettingsRow
                        label={
                            <span role="status">
                                {t('settings.ownOrganisations.empty', 'Noch keine eigene Organisation ausgewählt.')}
                            </span>
                        }
                    />
                ) : (
                    options.map((option) => (
                        <SettingsRow key={option.id} label={option.label} description={option.sublabel || undefined}>
                            <Button
                                size="sm"
                                variant="outline"
                                aria-label={`${option.label} ${t('settings.ownOrganisations.removeAria', 'entfernen')}`}
                                disabled={busyIds.has(option.id)}
                                onClick={() => flagMutation.mutate({ option, flagged: false })}
                            >
                                {t('settings.ownOrganisations.remove', 'Entfernen')}
                            </Button>
                        </SettingsRow>
                    ))
                )}
            </SettingsSection>

            <SettingsSection
                title={t('settings.ownOrganisations.addTitle', 'Organisation hinzufügen')}
                description={t(
                    'settings.ownOrganisations.hint',
                    'Auswahl aus den Organisationen, die bereits im Betriebsabbild vorhanden sind.'
                )}
            >
                <SettingsRow
                    label={t('settings.ownOrganisations.searchLabel', 'Organisation suchen')}
                    htmlFor="own-org-search"
                    description={
                        tooShort ? (
                            <span role="status">
                                {t(
                                    'settings.ownOrganisations.tooShort',
                                    `Mindestens ${OWN_ORG_MIN_QUERY_LENGTH} Zeichen eingeben.`,
                                )}
                            </span>
                        ) : undefined
                    }
                    field
                >
                    <Input
                        id="own-org-search"
                        placeholder={t('settings.ownOrganisations.searchPlaceholder', 'Organisation suchen …')}
                        value={raw}
                        onChange={(event) => setRaw(event.target.value)}
                        // Enter is THE search gesture here, and this input may sit
                        // inside a form on some layouts; swallowing it keeps a typed
                        // name from submitting something else entirely.
                        onKeyDown={(event) => {
                            if (event.key === 'Enter') event.preventDefault();
                        }}
                    />
                </SettingsRow>
                {term && (
                    <SearchResults
                        term={term}
                        excludedIds={flaggedIds}
                        busyIds={busyIds}
                        onAdd={(option) => flagMutation.mutate({ option, flagged: true })}
                    />
                )}
            </SettingsSection>
        </div>
    );
}
